import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, CheckCircle, Lightning, XCircle } from "@phosphor-icons/react";

export default function ParentBilling() {
  const [invoices, setInvoices] = useState([]);
  const [autopay, setAutopay] = useState(null);
  const [busy, setBusy] = useState("");
  const [params] = useSearchParams();
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [i, a] = await Promise.all([api.get("/invoices"), api.get("/autopay")]);
    setInvoices(i.data);
    setAutopay(a.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Returning from the Stripe card-saving flow.
  useEffect(() => {
    const sid = params.get("autopay_session");
    if (!sid) return;
    (async () => {
      try {
        await api.post(`/autopay/confirm/${sid}`);
        toast.success("Autopay is on — tuition will be charged automatically each Friday");
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail));
      }
      nav("/portal/billing", { replace: true });
      load();
    })();
  }, [params, nav, load]);

  const pay = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/payments/checkout", { invoice_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const enroll = async () => {
    setBusy("autopay");
    try {
      const { data } = await api.post("/autopay/setup", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const turnOff = async () => {
    try {
      await api.delete("/autopay");
      toast.success("Autopay turned off");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const due = invoices.filter((i) => i.status === "unpaid");
  const total = due.reduce((a, i) => a + Number(i.amount), 0);

  return (
    <div data-testid="parent-billing">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Tuition</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Your invoices</h1>

      <div className="mt-8 grid lg:grid-cols-2 gap-4 max-w-3xl">
        <div className="bg-[#0F172A] text-white rounded-3xl p-8" data-testid="balance-card">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-white/60">Balance due</p>
          <p className="mt-3 font-display text-5xl font-bold">{money(total)}</p>
          <p className="mt-2 text-sm text-white/60">{due.length} open invoice{due.length === 1 ? "" : "s"}</p>
        </div>

        <div className="bg-white rounded-3xl border-2 border-border p-8" data-testid="autopay-card">
          <span className="h-12 w-12 rounded-2xl bg-amber-50 text-[#F59E0B] grid place-items-center">
            <Lightning size={24} weight="duotone" />
          </span>
          {autopay?.enabled ? (
            <>
              <p className="mt-5 font-display text-xl font-semibold">Autopay is on</p>
              <p className="mt-2 text-sm text-muted-foreground" data-testid="autopay-card-details">
                {autopay.brand?.toUpperCase()} •••• {autopay.last4}
                {autopay.exp ? ` · expires ${autopay.exp}` : ""}
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Friday tuition is charged automatically. We'll email you a receipt each time.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button variant="outline" onClick={turnOff} className="rounded-full font-bold h-11 px-6"
                        data-testid="autopay-off-btn"><XCircle size={17} className="mr-2" /> Turn off</Button>
                <Button variant="outline" onClick={enroll} className="rounded-full font-bold h-11 px-6"
                        data-testid="autopay-update-btn">Update card</Button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-5 font-display text-xl font-semibold">Set up autopay</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Save your card once and weekly tuition is paid automatically every Friday — no more reminders
                to chase. Cancel any time.
              </p>
              <Button onClick={enroll} disabled={busy === "autopay"}
                      className="mt-5 h-11 px-7 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold active:scale-95"
                      style={{ transition: "transform .2s ease, background-color .2s ease" }}
                      data-testid="autopay-enroll-btn">
                <CreditCard size={18} className="mr-2" />
                {busy === "autopay" ? "Redirecting…" : "Save my card"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-8 space-y-3" data-testid="parent-invoices-list">
        {invoices.length === 0 && <p className="text-muted-foreground">No invoices yet.</p>}
        {invoices.map((i) => (
          <div key={i.id} className="bg-white rounded-2xl border border-border p-6 flex flex-wrap gap-4 items-center"
               data-testid={`parent-invoice-${i.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-bold">{i.number} · {i.student_name}</p>
              <p className="text-xs text-muted-foreground mt-1 font-semibold">
                {i.description} · due {i.due_date}
                {i.method === "autopay" ? " · paid by autopay" : ""}
              </p>
            </div>
            <p className="font-display text-2xl font-bold">{money(i.amount)}</p>
            {i.status === "paid" ? (
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#10B981]" data-testid={`paid-badge-${i.id}`}>
                <CheckCircle size={20} weight="fill" /> Paid
              </span>
            ) : i.status === "void" ? (
              <span className="text-sm font-bold text-muted-foreground">Voided</span>
            ) : (
              <Button onClick={() => pay(i.id)} disabled={busy === i.id}
                      className="rounded-full h-12 px-7 bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold active:scale-95"
                      style={{ transition: "transform .2s ease, background-color .2s ease" }}
                      data-testid={`pay-invoice-${i.id}`}>
                <CreditCard size={18} className="mr-2" /> {busy === i.id ? "Redirecting…" : "Pay now"}
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="mt-8 text-sm text-muted-foreground max-w-xl">
        Payments are processed securely by Stripe — we never see or store your card number. Vouchers, cash and
        checks can also be recorded by the front office: call (501) 904-7103.
      </p>
    </div>
  );
}
